/**************************************
 * request.js
 * Vue3 + Axios 统一请求封装
 **************************************/

import axios from "axios";
import code200Error from "./code200Error.js";

const WHITE_LIST = ["/auth/oauth2/token"];

// 是否开发环境
const IS_DEV = process.env.NODE_ENV === "development";

// 请求队列（用于防重）
const pendingRequests = new Map();

// 401 跳转锁
let isRedirectingLogin = false;

// 创建 Axios 实例
const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || "",
  timeout: process.env.VUE_APP_TIMEOUT || 600000, // 10分钟超时
  headers: {
    "Content-Type": "application/json",
    ormplatformcode: "YPLAN",
    formplatformid: 8,
    tenantid: 2,
  },
});

// 生成稳定的请求 Key（防重）
function generateRequestKey(config) {
  if (!config) return "";

  const { method, url, data, params } = config;

  const sortedData =
    data && typeof data === "object"
      ? JSON.stringify(data, Object.keys(data).sort())
      : data;

  const sortedParams =
    params && typeof params === "object"
      ? JSON.stringify(params, Object.keys(params).sort())
      : params;

  return `${method}:${url}:${sortedData}:${sortedParams}`;
}

// Token 处理
async function getToken() {
  try {
    return localStorage.getItem("userToken");
  } catch {
    return null;
  }
}

// 日志（仅开发环境）
function logRequest(config) {
  if (!IS_DEV) return;
  console.log(
    `[REQUEST] ${config.method?.toUpperCase()} ${config.url}`,
    config.data || config.params,
  );
}

function logResponse(url, data) {
  if (!IS_DEV) return;
  console.log(`[RESPONSE] ${url}`, data);
}

// 错误处理中心
function handleBusinessError(data) {
  const errMsg =
    code200Error[data.code]?.[0] || data.msg || "系统异常，请稍后重试";

  // 可在这里做全局 Toast / Modal 控制
  if (typeof uni !== "undefined") {
    uni.showToast({ title: errMsg, icon: "none" });
  } else {
    console.error(errMsg);
  }
}

// 401 登录失效处理
function handleTokenExpired() {
  if (isRedirectingLogin) return;

  isRedirectingLogin = true;

  localStorage.removeItem("loginUser");
  localStorage.removeItem("userInfo");
}

// HTTP 状态码处理
function handleHttpError(error, reject) {
  const { config, response } = error;

  if (response) {
    const { status: statusCode, data } = response;
    const msg = data?.msg || "请求失败";

    switch (statusCode) {
      case 401:
        handleTokenExpired();
        break;

      case 403:
        console.error(config.url, "暂无权限");
        break;

      case 400:
        console.error(config.url, msg);
        break;

      case 500:
        console.error("服务器异常");
        break;

      case 502:
      case 503:
      case 504:
        console.error("服务不可用");
        break;

      default:
        console.error(config.url, msg);
    }
    reject({ statusCode, data });
  } else {
    console.error("网络请求失败:", error.message);
    reject(error);
  }
}

// 请求拦截器
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 防重处理
    const requestKey = generateRequestKey(config);

    if (pendingRequests.has(requestKey) && !config.allowDuplicate) {
      return config;
    }

    pendingRequests.set(requestKey, true);

    logRequest(config);

    return config;
  },
  (error) => {
    if (error?.config) {
      const requestKey = error?.config
        ? generateRequestKey(error.config)
        : null;
      pendingRequests.delete(requestKey);
    }
    return Promise.reject(error);
  },
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  (response) => {
    const { config, data } = response;
    const requestKey = generateRequestKey(config);

    pendingRequests.delete(requestKey);
    logResponse(config.url, data);

    // 业务错误处理（success 为 false）
    if (data && !data.success && !WHITE_LIST.includes(config.url)) {
      handleBusinessError(data);
    }

    return data;
  },
  (error) => {
    if (error?.config) {
      const requestKey = error?.config
        ? generateRequestKey(error.config)
        : null;
      pendingRequests.delete(requestKey);
    }
    return Promise.reject(error);
  },
);

// 封装调用 对应 method API
export function get(url, params = {}, config = {}) {
  return axiosInstance.get(url, { params, ...config });
}

export function post(url, data = {}, config = {}) {
  return axiosInstance.post(url, data, config);
}

export function put(url, data = {}, config = {}) {
  return axiosInstance.put(url, data, config);
}

export function del(url, params = {}, config = {}) {
  return axiosInstance.delete(url, { params, ...config });
}

export default { get, post, put, del, axiosInstance };
